import React, { useState } from 'react'
import useElementSize from 'hooks/useElementSize'

import { updateAnswer } from 'store/actions'
import { useQuizContext } from 'store/QuizStore'
import { FluidBox, ResultLink, HintWrapper } from 'styled'

import { Root, Pie, OptionsRow, DonutButton, SlicePath } from './PieQuestion.styled.js'

const PIE_RADIUS = 0.4

export const toPct = str => Number(str.replace('%', '')) / 100

const generateSliceData = data => {
  const arcs = []
  data.forEach((val, id) => {
    const pct = toPct(val)
    const ang = pct * Math.PI * 2
    arcs[id] = { ang, pct, val }
  })
  return arcs
}

const getSlice = ({ ang }, radius) => {
  const x = Math.cos(ang) * radius
  const y = Math.sin(ang) * radius
  return `M0 0L${radius} 0A${radius} ${radius} 0 ${ang > Math.PI ? 1 : 0} 1 ${x} ${y}z`
}

const Slice = ({ data, radius, highlight, selected }) => {
  const d = getSlice(data, radius)

  return (
    <g>
      <SlicePath d={d} mode="outline" highlight={highlight}></SlicePath>
      <SlicePath d={d} mode="selection" highlight={highlight} selected={selected}></SlicePath>
    </g>
  )
}

const Slice360 = ({ radius }) => {
  const x = Math.cos(Math.PI * 2) * radius
  const y = Math.sin(Math.PI * 2) * radius
  const d = `M0 0L${radius} 0A${radius} ${radius} 0 1 1 ${x} ${y}`

  return (
    <g>
      <SlicePath d={d} mode="outline"></SlicePath>
    </g>
  )
}

const PieQuestion = ({ options, answer, answerInfo, visited, entering, leaving, navDirection }) => {
  const { dispatch } = useQuizContext()
  const [hoverPct, setHoverPct] = useState()
  const [innerRef, { width, height }] = useElementSize()

  const arcsData = generateSliceData([...options].sort((a, b) => toPct(a) - toPct(b)))
  const radius = Math.min(width, height) * PIE_RADIUS

  return (
    <Root visited={visited}>
      <Pie py="spaceNormal" entering={entering} leaving={leaving}>
        <div className="Inner" ref={innerRef}>
          <svg viewBox={`0 0 ${width} ${height}`}>
            <g
              fill="none"
              strokeLinecap="round"
              transform={`translate(${width / 2}, ${height / 2}) rotate(-90)`}
            >
              <Slice360 radius={radius}></Slice360>
              {arcsData.reverse().map((arc, id) => (
                <Slice
                  key={id}
                  data={arc}
                  radius={radius}
                  highlight={arc.pct <= hoverPct}
                  selected={answer !== null && arc.pct <= toPct(options[answer])}
                ></Slice>
              ))}
            </g>
          </svg>
        </div>
      </Pie>
      <OptionsRow mb="spaceNormal">
        {options.map((option, id) => (
          <DonutButton
            key={id}
            nth={id}
            selected={answer === id}
            onClick={() => updateAnswer(dispatch, id)}
            onMouseOver={() => setHoverPct(toPct(option))}
            onMouseOut={() => setHoverPct(null)}
            entering={entering}
            leaving={leaving}
            navDirection={navDirection}
          >
            {option}
          </DonutButton>
        ))}
      </OptionsRow>
      <HintWrapper entering={entering} leaving={leaving} navDirection={navDirection}>
        {!visited ? (
          <FluidBox fs="textSmall" color="darkGrey" mb="spaceNormal">
            Select one of the options
          </FluidBox>
        ) : (
          <ResultLink
            href={answerInfo.source.url || null}
            as={answerInfo.source.url ? 'a' : 'div'}
            target={answerInfo.source.url ? '_blank' : undefined}
            rel="noopener noreferrer"
            fs={12}
            align="center"
            color="primary"
            mb="spaceNormal"
          >
            {answerInfo.source.text}
          </ResultLink>
        )}
      </HintWrapper>
    </Root>
  )
}

export default PieQuestion
